import React, { useEffect } from 'react';
import {
    useTable,
    useSortBy,
    usePagination,
    useExpanded,
    useGlobalFilter,
    Column,
    TableInstance,
} from 'react-table';
import BTable from 'react-bootstrap/Table';
import './ReactTableStyle.css';
import _ from 'lodash';
import PaginationButtonGroups from './PaginationButtonGroups';
import GlobalFilter from './GlobalFilter';
import { generateSortingIndicator } from '../listUtils';
import { DEFAULT_INITIAL_STATE } from '../../../utils/Constants';
import { initialStateType } from './DataGrid';
import usePrevious from '../../../hooks/usePrevious';

export type DataTableProps<T extends object> = {
    columns: Array<Column<T>>;
    data: Array<T>;
    initialState?: initialStateType;
    setInitialState?: Function;
    renderRowSubComponent?: Function;
    noGlobalFilter?: boolean;
    noPagination?: boolean;
    getInstanceCallback?: (instance: TableInstance<T>) => void;
};

const DataTable = <T extends object>({
    columns,
    data,
    initialState,
    setInitialState,
    renderRowSubComponent,
    noGlobalFilter,
    noPagination,
    getInstanceCallback,
}: DataTableProps<T>) => {
    const tableInstance = useTable(
        {
            columns,
            data,
            initialState: initialState || DEFAULT_INITIAL_STATE,
            getInstanceCallback,
        },
        useGlobalFilter,
        useSortBy,
        useExpanded,
        usePagination,
        (hooks) =>
            hooks.useInstance.push((instance) => {
                if (instance && instance.getInstanceCallback) {
                    instance.getInstanceCallback(instance);
                }
            }),
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        visibleColumns,
        state: { pageIndex, pageSize, sortBy },
        state,
        preGlobalFilteredRows,
        setGlobalFilter,
    } = tableInstance;

    const previousState = usePrevious(initialState);

    useEffect(() => {
        const newState = {
            pageIndex,
            pageSize,
            sortBy,
        };

        if (!_.isEqual(previousState, newState) && setInitialState) {
            setInitialState(JSON.stringify(newState));
        }
    }, [sortBy, pageIndex, pageSize]);

    return (
        <>
            {!noGlobalFilter && (
                <GlobalFilter
                    preGlobalFilteredRows={preGlobalFilteredRows}
                    globalFilter={state.globalFilter}
                    setGlobalFilter={setGlobalFilter}
                />
            )}
            <BTable {...getTableProps()}>
                <thead>
                    {headerGroups.map((headerGroup) => (
                        <tr {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map((column) => (
                                <th
                                    {...column.getHeaderProps(
                                        column.getSortByToggleProps({
                                            style: {
                                                minWidth: column.minWidth,
                                                width: column.width,
                                            },
                                        }),
                                    )}
                                >
                                    {column.render('Header')}{' '}
                                    {column.canSort && (
                                        <span>
                                            {column.disableSortBy
                                                ? ''
                                                : generateSortingIndicator(
                                                      column.isSorted,
                                                      column.isSortedDesc,
                                                  )}
                                        </span>
                                    )}
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                    {page.map((row) => {
                        prepareRow(row);
                        return (
                            <React.Fragment key={row.index}>
                                <tr {...row.getRowProps({})}>
                                    {row.cells.map((cell) => (
                                        <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                                    ))}
                                </tr>
                                {row.isExpanded && renderRowSubComponent ? (
                                    <tr className="sub-component">
                                        <td className="px-3 py-0" colSpan={visibleColumns.length}>
                                            {renderRowSubComponent({ row })}
                                        </td>
                                    </tr>
                                ) : null}
                            </React.Fragment>
                        );
                    })}
                    {page.length <= 0 && (
                        <tr>
                            <td colSpan={1000} className="text-center">
                                <i>No data to display.</i>
                            </td>
                        </tr>
                    )}
                </tbody>
            </BTable>

            {!noPagination && (
                <PaginationButtonGroups
                    canPreviousPage={canPreviousPage}
                    canNextPage={canNextPage}
                    pageCount={pageCount}
                    pageOptions={pageOptions}
                    pageIndex={pageIndex}
                    gotoPage={gotoPage}
                    previousPage={previousPage}
                    nextPage={nextPage}
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                />
            )}
        </>
    );
};

export default DataTable;
